<template>
  <section :class="$style.editor">
    <header :class="$style.header">
      <p :class="$style.header__text">{{ modalTitle }}</p>
      <FlatButton
        v-if="shouldShowDeleteButton"
        size="small"
        variant="caution"
        :class="$style.header__button"
        :disabled="disabledSubmit"
        @click="clickDelete"
      >
        <template #icon>
          <IconDustBox :class="$style.header__icon" />
        </template>
        この日程を削除
      </FlatButton>
    </header>
    <p v-if="!isEditable" :class="$style.form__notEditable">
      こちらの日程にはすでにお申込みがあります。<br />
      申込みのある日程は編集・削除できません。
    </p>
    <div :class="[$style.editor__form, $style.form]">
      <div :class="$style.form__item">
        <p :class="[$style.form__title, $style.title]">タイトル</p>
        <p :class="[$style.form__value, $style['form__value--bold']]">
          {{ title }}
        </p>
      </div>
      <div :class="$style.form__item">
        <p :class="[$style.form__title, $style.title]">開催日時</p>
        <div :class="$style.form__value">
          <DolphinDateInput
            :inputClassName="$style.form__date"
            :minutesIncrement="15"
            :minDate="minDate"
            :disabled="!isEditable"
            :flow="['calendar', 'time']"
            v-model="form.startedAt"
          />
        </div>
      </div>
      <div :class="$style.form__item">
        <div :class="[$style.form__title, $style.title]">
          <p>終了日時</p>
          <p v-show="isInvalidEndedAt" :class="$style.notice">
            開催日時より後にする必要があります
          </p>
        </div>
        <div :class="$style.form__value">
          <DolphinDateInput
            :inputClassName="$style.form__date"
            :minutesIncrement="15"
            :disabled="!isEditable"
            :flow="['calendar', 'time']"
            v-model="form.endedAt"
          />
        </div>
      </div>
      <div :class="$style.form__item">
        <div :class="[$style.form__title, $style.title]">
          <p>開催場所</p>
          <p v-show="isInvalidLocation" :class="$style.notice">必須項目です</p>
        </div>
        <div :class="[$style.form__value, $style['withCheck']]">
          <DolphinTextInput
            :class="$style.withCheck__input"
            :disabled="!isEditable"
            v-model="form.location"
          />
          <div :class="$style.withCheck__check">
            <DolphinCheckbox v-model="form.isOnline" :disabled="!isEditable" />
            <span>オンライン開催</span>
          </div>
        </div>
      </div>
      <div :class="$style.form__item">
        <div :class="[$style.form__title, $style.title]">
          <p>定員</p>
          <p v-show="isInvalidCapacity" :class="$style.notice">必須項目です</p>
        </div>
        <div :class="[$style.form__value, $style['withCheck']]">
          <DolphinTextInput
            :class="$style.withCheck__input"
            :disabled="form.isNoCapacity || !isEditable"
            type="number"
            min="1"
            v-model="form.capacity"
          />
          <div :class="$style.withCheck__check">
            <DolphinCheckbox
              v-model="form.isNoCapacity"
              :disabled="!isEditable"
            />
            <span>制限なし</span>
          </div>
        </div>
      </div>
      <div :class="$style.form__item">
        <div :class="[$style.form__title, $style.title]">
          <p>申込み締切日時</p>
          <p v-show="isInvalidApplicationDue" :class="$style.notice">
            開催日時より前にする必要があります
          </p>
        </div>
        <div :class="$style.form__value">
          <DolphinDateInput
            :minutesIncrement="15"
            :inputClassName="$style.form__date"
            :disabled="!isEditable"
            :flow="['calendar', 'time']"
            v-model="form.applicationDue"
          />
        </div>
      </div>
      <div :class="$style.form__submit">
        <FlatButton
          :disabled="isDisabled"
          variant="primary"
          size="medium"
          @click="clickConfirm"
        >
          {{ confirmTitle }}
        </FlatButton>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
type Form = {
  id: string | null
  startedAt: Date | null
  endedAt: Date | null
  location: string
  isOnline: boolean
  capacity: string | number
  isNoCapacity: boolean
  applicationDue: Date | null
}

export type SubmittedForm = Omit<
  Form,
  'startedAt' | 'endedAt' | 'applicationDue'
> & {
  startedAt: Date
  endedAt: Date
  applicationDue: Date
}
</script>

<script lang="ts" setup>
import { ref, watch, computed } from 'vue'
import type { EditSeminarScheduleSeminarScheduleFragment } from './EditSeminarSchedule.generated'
import DolphinCheckbox from '@/components/atoms/DolphinCheckbox.vue'
import DolphinTextInput from '@/components/atoms/DolphinTextInput.vue'
import DolphinDateInput from '@/components/atoms/DolphinDateInput.vue'
import { parseISO, addHours, startOfDay } from 'date-fns'
import IconDustBox from '@/components/icons/IconDustBox.vue'
import FlatButton from '@/components/atoms/FlatButton.vue'

const emit = defineEmits<{
  (event: 'create', data: SubmittedForm): void
  (event: 'update', data: SubmittedForm): void
  (event: 'delete', id: EditSeminarScheduleSeminarScheduleFragment['id']): void
}>()

const props = defineProps<{
  title: string
  date: Date
  disabledSubmit: boolean
  schedule: EditSeminarScheduleSeminarScheduleFragment | null
}>()

const isEditable = computed(
  () => !props.schedule || props.schedule.applicantCount === 0
)

const shouldShowDeleteButton = computed(() => form.value.id && isEditable.value)

const getDefault = (): Form => ({
  id: null,
  startedAt: props.date,
  endedAt: addHours(props.date, 1),
  location: '',
  isOnline: true,
  capacity: '1',
  isNoCapacity: false,
  applicationDue: addHours(props.date, -24),
})

const form = ref<Form>(getDefault())

watch(
  () => props.schedule,
  value => {
    form.value =
      value != null
        ? {
            id: value.id,
            startedAt: parseISO(value.startedAt),
            endedAt: parseISO(value.endedAt),
            location: value.location,
            isOnline: value.isOnline,
            capacity: value.capacity || '',
            isNoCapacity: !value.capacity,
            applicationDue: parseISO(value.applicationDue),
          }
        : getDefault()
  },
  { immediate: true }
)

watch(
  () => form.value.isNoCapacity,
  value => {
    form.value = {
      ...form.value,
      capacity: value ? '' : form.value.capacity,
    }
  }
)

watch(
  () => form.value.startedAt,
  value => {
    if (value) {
      form.value = {
        ...form.value,
        endedAt: addHours(value, 1),
        applicationDue: addHours(value, -24),
      }
    }
  }
)

const minDate = startOfDay(new Date())

const isUpdate = computed(() => !!props.schedule?.id)

const confirmTitle = computed(() =>
  isUpdate.value ? '編集を確定' : 'この内容で日程を追加'
)

const modalTitle = computed(() =>
  isUpdate.value ? '日程の編集・削除' : '日程の追加'
)

const isInvalidStartedAt = computed(() => {
  // 挙動上 null にならないが、型では nullable なので一応チェックしておく
  return form.value.startedAt == null
})

const isInvalidEndedAt = computed(() => {
  if (form.value.startedAt == null || form.value.endedAt == null) {
    return true
  }
  return form.value.endedAt < form.value.startedAt
})

const isInvalidLocation = computed(() => form.value.location.length === 0)

const isInvalidCapacity = computed(
  () => !form.value.isNoCapacity && [0, ''].includes(form.value.capacity)
)

const isInvalidApplicationDue = computed(() => {
  if (form.value.startedAt == null || form.value.applicationDue == null) {
    return true
  }
  return form.value.applicationDue >= form.value.startedAt
})

const isDisabled = computed(
  () =>
    !isEditable.value ||
    isInvalidStartedAt.value ||
    isInvalidEndedAt.value ||
    isInvalidLocation.value ||
    isInvalidCapacity.value ||
    isInvalidApplicationDue.value ||
    props.disabledSubmit
)

const clickDelete = () => {
  if (!form.value.id) {
    return
  }

  emit('delete', form.value.id)
}

const clickConfirm = () => {
  if (
    form.value.startedAt == null ||
    form.value.endedAt == null ||
    form.value.applicationDue == null
  ) {
    return
  }

  const submittedForm: SubmittedForm = {
    ...form.value,
    startedAt: form.value.startedAt,
    endedAt: form.value.endedAt,
    applicationDue: form.value.applicationDue,
  }

  if (isUpdate.value) {
    emit('update', submittedForm)

    return
  }

  emit('create', submittedForm)
}
</script>

<style lang="scss" module>
.editor {
  min-width: 500px;
  padding: 20px;

  &__form {
    margin-top: 20px;
  }
}

.form {
  &__item {
    &:nth-child(n + 2) {
      margin-top: 20px;
    }
  }

  &__value {
    margin-top: 10px;

    &--bold {
      font-weight: bold;
    }
  }

  &__date {
    height: 50px;
  }

  &__submit {
    display: flex;
    justify-content: center;
    margin-top: 20px;
  }

  &__notEditable {
    margin-top: 24px;
    padding: 8px 12px;
    background-color: $color-caution-surface;
    border-radius: 4px;
    color: $color-caution-accent;
  }
}

.title {
  font-weight: bold;
  display: flex;
  gap: 20px;
}

.notice {
  color: $color-caution-accent;
}

.withCheck {
  display: flex;
  align-items: center;
  gap: 20px;

  &__input {
    flex: 1;
  }

  &__check {
    width: 130px;
    gap: 4px;
    display: flex;
    align-items: center;
  }
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;

  &__text {
    font-size: 20px;
    font-weight: bold;
  }

  &__button {
    align-items: flex-start;
  }
}
</style>
